/**
 * Climb volume — "how much was climbed", routes and Spraywall together.
 *
 * Volume is a route send or repeat plus a spray send or repeat. A spray
 * project is not a climb. Spray never adds points: points come from `ascents`
 * only, so this module never touches them.
 *
 * Kept apart from `activityDates` on purpose: that union already holds
 * `sprayDates` (every spray entry, projects included), so adding the spray
 * volume to it would count the same entries twice in one sum.
 *
 * Spec: docs/spec/02e-shared-activity-stats.md ("Climb volume (climbVolume.js)").
 */
import { sprayDayLogEntries } from "./sprayDayLog.js";
import { bandOfGrade } from "./gradeBands.js";

const isMap = (v) => Object.prototype.toString.call(v) === "[object Object]";
const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const inRange = (day, from, to) => (from == null || day >= from) && (to == null || day <= to);

/** The spray volume of one `sprayDayStats` day: sends + repeats (a day written before `r` existed counts its `s`). */
function sprayDayVolume(stat) {
  if (!isMap(stat)) return 0;
  return positive(stat.s) + positive(stat.r);
}

/**
 * The per-day volume map `{ date: route climbs + spray sends + spray repeats }`.
 * A day with no positive contribution is absent.
 */
export function volumeDates({ climbDates = null, sprayDayStats = null } = {}) {
  const out = {};
  if (isMap(climbDates)) {
    for (const [day, v] of Object.entries(climbDates)) {
      const n = positive(v);
      if (n > 0) out[day] = (out[day] || 0) + n;
    }
  }
  if (isMap(sprayDayStats)) {
    for (const [day, stat] of Object.entries(sprayDayStats)) {
      const n = sprayDayVolume(stat);
      if (n > 0) out[day] = (out[day] || 0) + n;
    }
  }
  return out;
}

/** The spray sends and repeats of `sprayDayStats` in `[from, to]` (inclusive, `null` = open). */
export function sprayVolumeInRange(sprayDayStats, from = null, to = null) {
  let sends = 0;
  let repeats = 0;
  if (isMap(sprayDayStats)) {
    for (const [day, stat] of Object.entries(sprayDayStats)) {
      if (!inRange(day, from, to) || !isMap(stat)) continue;
      sends += positive(stat.s);
      repeats += positive(stat.r);
    }
  }
  return { climbs: sends + repeats, sends, repeats };
}

/** The spray part of one daily climb-log document, counted from its valid `spray` entries. */
export function sprayLogTotals(log) {
  const totals = { climbs: 0, sends: 0, repeats: 0, projects: 0, flashes: 0 };
  for (const e of sprayDayLogEntries([log])) {
    if (e.kind === "send") {
      totals.sends += 1;
      if (e.flash) totals.flashes += 1;
    } else if (e.kind === "repeat") {
      totals.repeats += 1;
    } else if (e.kind === "project") {
      totals.projects += 1;
    }
  }
  totals.climbs = totals.sends + totals.repeats;
  return totals;
}

/**
 * The volume of one daily climb-log document, route and spray parts apart so a
 * reader can draw the spray part as its own slice. The route part is the
 * document's `totals` (route-only by construction). Under the lead filter the
 * spray part is 0: the spray wall is bouldering.
 */
export function logVolume(log, { discipline = null } = {}) {
  const t = isMap(log) && isMap(log.totals) ? log.totals : {};
  const route = {
    climbs: positive(t.climbs),
    sends: positive(t.sends),
    repeats: positive(t.repeats),
  };
  const spray = discipline === "lead"
    ? { climbs: 0, sends: 0, repeats: 0 }
    : (({ climbs, sends, repeats }) => ({ climbs, sends, repeats }))(sprayLogTotals(log));
  return {
    climbs: route.climbs + spray.climbs,
    sends: route.sends + spray.sends,
    repeats: route.repeats + spray.repeats,
    route,
    spray,
  };
}

/**
 * The spray sends and repeats of one daily climb-log document per colour band
 * (`bands` of `gradeBands`): `{ [bandKey]: n }`. An entry whose grade lies in
 * no band, or has no integer `gradeIdx`, is left out.
 */
export function sprayVolumeByBand(log, bands) {
  const out = {};
  for (const e of sprayDayLogEntries([log])) {
    if (e.kind !== "send" && e.kind !== "repeat") continue;
    const band = bandOfGrade(bands, e.gradeIdx);
    if (!band) continue;
    out[band.key] = (out[band.key] || 0) + 1;
  }
  return out;
}
